import { forwardRef, useEffect, useRef } from 'react';
import {
  Dialog,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Slide,
  Box,
  CircularProgress,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { styled } from '@mui/material/styles';

// Transición para el diálogo
const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction='up' ref={ref} {...props} />;
});

// Estilos personalizados usando Material-UI
const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    zIndex: theme.zIndex.modal + 1,
    margin: 0,
    width: '100%',
    height: '100%',
    maxWidth: 'none',
    maxHeight: 'none',
    borderRadius: 0,
  },
  '& .MuiBackdrop-root': {
    zIndex: theme.zIndex.modal,
  },
}));

const StyledAppBar = styled(AppBar)(({ background }) => ({
  position: 'relative',
  backgroundColor: background || '#1976d2',
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: theme.spacing(2.5),
  flex: 1,
  overflowY: 'auto',
  backgroundColor: '#fff',

  '& .package-search__form__container': {
    padding: 0,
    margin: 0,
    height: 'auto',
    minHeight: '100%',
  },

  '& .package-search__form__fields': {
    gap: theme.spacing(1),
    height: 'auto',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },

  '& .form__field': {
    marginBottom: theme.spacing(1),
    width: '100%',
    maxWidth: '100%',
  },

  '& .full-screen-dialog': {
    cursor: 'pointer',
    padding: theme.spacing(1.5),
    border: '1px solid #e0e0e0',
    borderRadius: theme.spacing(1),
    backgroundColor: '#f9f9f9',
    transition: 'all 0.2s ease',
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',
    display: 'block',
    textAlign: 'left',
    fontSize: '16px',
    minHeight: '48px',
    lineHeight: '1.5',

    '&:hover': {
      backgroundColor: '#f0f0f0',
      borderColor: '#007bff',
    },

    '&:focus': {
      outline: 'none',
      borderColor: '#007bff',
      boxShadow: '0 0 0 2px rgba(0, 123, 255, 0.25)',
    },
  },

  '& .package-search__button-row': {
    width: '100%',
    marginTop: theme.spacing(2),
  },

  '& .package-search__button': {
    width: '100%',
    maxWidth: '100%',
  },

  '& .form-field, & .form-control, & input, & select, & .MuiTextField-root': {
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',
  },

  '& .input-container': {
    width: '100%',
    marginBottom: theme.spacing(2),
  },

  '& .input-container h3': {
    marginBottom: theme.spacing(1),
    fontSize: '14px',
    fontWeight: '500',
    color: '#4f4f4f',
  },

  '& .MuiStack-root, & .MuiBox-root, & .MuiGrid-root': {
    width: '100%',
  },

  '& .date-input, & .passengers-input': {
    width: '100%',
    maxWidth: '100%',
  },

  '& .OrigenDestinoFullScreenDialog': {
    width: '100%',
  },

  '& .full-screen-dialog[onclick*="handleClickOpen"], & .full-screen-dialog[onclick*="handleManualOpen"]':
    {
      width: '100%',
      maxWidth: '100%',
      boxSizing: 'border-box',
    },
}));

const StyledDialogActions = styled(DialogActions)(({ theme }) => ({
  padding: theme.spacing(2, 2.5),
  backgroundColor: '#fff',
  borderTop: '1px solid #e0e0e0',
  position: 'sticky',
  bottom: 0,
  zIndex: 1,
}));

const SearchButton = styled(Button)(({ theme, backgroundcolor, textcolor }) => ({
  width: '100%',
  padding: theme.spacing(1.5, 3),
  backgroundColor: backgroundcolor || '#1976d2',
  color: textcolor || '#fff',
  fontSize: '16px',
  fontWeight: 600,
  borderRadius: theme.spacing(1),
  textTransform: 'none',

  '&:hover': {
    backgroundColor: backgroundcolor || '#1976d2',
    opacity: 0.9,
  },

  '&:disabled': {
    backgroundColor: '#ccc',
    color: '#666',
  },
}));

const MobileSearchDialog = ({
  open,
  onClose,
  title,
  background,
  textColor,
  isLoading = false,
  onSearch,
  searchButtonText = 'Buscar',
  isSearchDisabled = false,
  children,
}) => {
  const firstFocusableRef = useRef(null);

  useEffect(() => {
    if (open && firstFocusableRef.current) {
      const timer = setTimeout(() => {
        firstFocusableRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [open]);
  return (
    <StyledDialog
      fullScreen
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
      disableEnforceFocus={false}
      disableAutoFocus={false}
      disableRestoreFocus={false}
      keepMounted={false}
      className='mobile-search-dialog z-100'
      hideBackdrop={false}
      disablePortal={false} // Cambiar a false para usar el portal
      aria-labelledby='mobile-search-title'
      aria-describedby='mobile-search-description'
      role='dialog'
      aria-modal='true'
    >
      <StyledAppBar background={background}>
        <Toolbar>
          <IconButton
            ref={firstFocusableRef}
            edge='start'
            color='inherit'
            onClick={onClose}
            aria-label='close'
            disabled={isLoading}
            tabIndex={0}
          >
            <CloseIcon />
          </IconButton>
          <Typography id='mobile-search-title' sx={{ ml: 2, flex: 1 }} variant='h6' component='div'>
            {title}
          </Typography>
          {isLoading && <CircularProgress size={24} color='inherit' sx={{ ml: 2 }} />}
        </Toolbar>
      </StyledAppBar>

      <StyledDialogContent id='mobile-search-description'>{children}</StyledDialogContent>

      <StyledDialogActions>
        <SearchButton
          onClick={onSearch}
          disabled={isSearchDisabled || isLoading}
          backgroundcolor={background}
          textcolor={textColor}
          startIcon={isLoading ? <CircularProgress size={20} color='inherit' /> : <SearchIcon />}
        >
          {isLoading ? 'Buscando...' : searchButtonText}
        </SearchButton>
      </StyledDialogActions>
    </StyledDialog>
  );
};

export default MobileSearchDialog;
